/* Copyright (c) 2020-present CloudIO Inc */

import { getDefaultValues } from '../../layout/Sidebar/panels/PropertiesView/CommonProperties';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { getManager } from '../../registry/Manager';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { propertyValueTypes } from '../../registry/constants';
import { isEmpty, isNotEmpty } from '../../utils/isEmpty';
import { ActionUpdateProp, defaultValue } from './ActionUpdateProp';
import { getPropertyDef } from 'layout/Sidebar/panels/PropertiesView/property-editors/PropertyEditorUtils';

const ActionUpdatePropManager: ActionManagerFn<'ActionUpdateProp'> = (
  platform: Platform,
): ActionManager<'ActionUpdateProp'> => {
  return {
    ...baseActionManager(platform, 'ActionUpdateProp'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Item ID',
        type: 'ItemID',
        required: true,
        helpText:
          'Choose the Item ID for which the property need to be updated.',
      },
      property: {
        label: 'Property',
        type: 'PropertyChooser',
        required: true,
        itemIdProp: 'targetItemId',
      },
      propertyValueType: {
        label: 'Value Type',
        type: 'select',
        options: propertyValueTypes,
        required: true,
        helpText: `Use Static if you want to update the property value as if it was updated in design mode. i.e. any expression used below will be evaluated at runtime during the component render cycle.

Use Expression if you want to update the property value to a specific value, any expression used below will be evaluated at the time of this action execution.`,
      },
      propertyValue: {
        label: 'Property Value',
        type: 'PropertyValue',
        itemIdProp: 'targetItemId',
        propertyProp: 'property',
        required: (item: RequiredItem<'ActionUpdateProp'>) =>
          item.props.propertyValueType === 'static',
      },
      propertyValueExpression: {
        label: 'Property Value Expression',
        type: 'expression',
        returnType: async (
          _item: RequiredItem<'ActionUpdateProp'>,
          { appUid, pageId, itemId },
        ) => {
          const item = platform.getItem(
            appUid,
            pageId,
            _item.props.targetItemId,
          );
          if (!item?.type) {
            return 'any';
          }
          const tmgr = getManager(item.type);
          const tdef = getPropertyDef(tmgr, _item.props.property);

          switch (tdef.type) {
            case 'boolean':
            case 'booleanExpression': {
              return 'boolean';
            }
            case 'number':
            case 'numberInput': {
              return 'number';
            }
            case 'string':
            case 'ColorExpression':
            case 'expressionOnly':
            case 'expression': {
              return 'string';
            }
            default:
              return 'any';
          }
        },
        required: (item: RequiredItem<'ActionUpdateProp'>) =>
          item.props.propertyValueType !== 'static',
      },
    },
    name: 'Update Property',
    getDisplayText: (item) =>
      `${item.props.property} of ${item.props.targetItemId}`,
    isPropertyVisible(
      prop,
      { targetItemId, property, propertyValueType },
    ): boolean {
      if (isEmpty(targetItemId) && prop !== 'targetItemId') {
        return false;
      }
      switch (prop) {
        case 'propertyValueType':
          return isNotEmpty(property);
        case 'propertyValue':
          return isNotEmpty(property) && propertyValueType === 'static';
        case 'propertyValueExpression':
          return isNotEmpty(property) && propertyValueType === 'expression';
        default:
          return true;
      }
    },
    onPropertyChange(
      property,
      value,
      props,
      { appUid, pageId, mgr },
    ): Partial<ActionUpdateProp> {
      if (property === 'targetItemId') {
        return {
          [property]: value,
          property: '',
          propertyValueType: props.propertyValueType || 'static',
          propertyValue: undefined,
          propertyValueExpression: { value: '', type: 'text' },
        };
      }
      if (property === 'property') {
        // replace propertyValue with default value whenever the property is changed
        // to avoid invalid value type being left out in propertyValue
        // which could cause runtime errors
        const item = platform.getItem(appUid, pageId, props.targetItemId);
        if (!item?.type) {
          return {
            [property]: value,
          };
        }
        const tmgr = getManager(item.type);
        const val = getDefaultValues<unknown>(tmgr, value as string);

        return {
          [property]: value,
          propertyValueType: props.propertyValueType || 'static',
          propertyValue: val,
          propertyValueExpression: { value: '', type: 'text' },
        };
      }
      return {
        [property]: value,
      };
    },
    icon: { iconName: 'edit' },
    description: `**Overview**<br/>
The Update Property action is a versatile dynamic control component that allows you to modify the properties of other UI components at runtime. While most component properties are set statically during design time, this action enables the application to reactively change behavior, appearance, or configuration—such as labels, colors, disability states, or active tabs—based on user interactions or logic flow.

**Key Features**<br/>
* **Universal Targeting:** Can target almost any property of any component on the page (e.g., changing a Button's label, a Text Field's disabled state, or a Chart's title).
* **Two-Mode Updates:** Offers distinct strategies for updating values:
    * *Static:* Replaces the property definition itself. If you provide an expression here, the target component will re-evaluate that expression continuously whenever it renders (reactive).
    * *Expression:* Evaluates the value once at the moment the action runs and pushes the static result to the target component (imperative).
* **Type Safety:** Automatically detects the data type of the selected property (e.g., Boolean, String, Number) and enforces valid input types in the expression editor.
* **Contextual UI:** The property editor dynamically adjusts to show only valid properties for the selected target item, preventing invalid configurations.

**Properties**<br/>
* **Item ID (targetItemId):** ItemID
  * *Description:* The unique identifier of the UI component you wish to modify.
* **Property (property):** PropertyChooser
  * *Description:* The specific attribute of the target component to update (e.g., label, visible, style).
* **Value Type (propertyValueType):** Select
  * *Options:* Static or Expression.
  * *Description:* Determines how the new value is applied.
    * *Static:* Sets the property as if you were editing it in the designer. Useful for applying new bindings.
    * *Expression:* Calculates the value immediately and sets the property to that specific result.
* **Property Value / Expression:**
  * *Description:* The new data to assign to the property. The input type changes based on the Value Type selected above.

**Use Cases**<br/>
* **Tab Navigation:** Programmatically switching tabs by updating the activeTab property of a Tab Container when a user completes a step in a wizard.
* **Dynamic Feedback:** Changing the label of a "Submit" button to "Processing..." and setting its disabled property to true immediately after it is clicked.
* **Conditional Styling:** Updating the style or className of a component to highlight it in red when a validation error occurs.
* **Resetting State:** Clearing the value of a specific text input field without resetting the entire form.`,
  };
};

export default ActionUpdatePropManager;
